type ProxyRulingPlanet = {
  planet?: string;
  proxyFor?: { planet?: string; via?: string[] }[];
  rejectedReasons?: string[];
};

type FruitfulResult = {
  rulingPlanets?: ProxyRulingPlanet[];
  rejectedRulingPlanets?: ProxyRulingPlanet[];
  judgmentNodeRepresentations?: Record<string, { source: string; representedPlanet: string }[]>;
  candidates?: {
    planet?: string;
    requiredEvidence?: unknown[];
    strongestLevelByHouse?: Record<string, unknown>;
  }[];
};

export type RulingPlanetProxyReason = { node: string; planet: string; path: string; isAcceptedProxy: boolean };

// This is a display union of the accepted RPs and positive evidence already in
// the result table. It does not assign a new score or change candidate tiers.
export function getFinalFruitfulPlanets(result?: FruitfulResult | null): string[] {
  const planets = [
    ...(result?.rulingPlanets || [])
      .filter((item) => !item.rejectedReasons?.length)
      .map((item) => item.planet),
    ...(result?.candidates || [])
      .filter((candidate) => candidate.requiredEvidence?.length
        || Object.keys(candidate.strongestLevelByHouse || {}).length)
      .map((candidate) => candidate.planet),
  ];
  const seen = new Set<string>();
  return planets.filter((planet): planet is string => {
    if (!planet?.trim()) return false;
    const key = planet.trim().toLowerCase();
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

export function getRulingPlanetProxyReasons(result?: FruitfulResult | null): RulingPlanetProxyReason[] {
  const reasons: RulingPlanetProxyReason[] = [];
  const seen = new Set<string>();
  const add = (node: string, planet: string, path: string, isAcceptedProxy: boolean) => {
    if (!node || !planet || !path) return;
    const key = [node, planet, path].join('|');
    if (seen.has(key)) return;
    seen.add(key);
    reasons.push({ node, planet, path, isAcceptedProxy });
  };
  (result?.rulingPlanets || []).forEach((item) => {
    (item.proxyFor || []).forEach((proxy) => {
      (proxy.via || []).forEach((path) => add(item.planet || '', proxy.planet || '', path, !item.rejectedReasons?.length));
    });
  });
  (result?.rejectedRulingPlanets || []).forEach((item) => {
    (item.proxyFor || []).forEach((proxy) => {
      (proxy.via || []).forEach((path) => add(item.planet || '', proxy.planet || '', path, false));
    });
  });
  Object.entries(result?.judgmentNodeRepresentations || {}).forEach(([node, paths]) => {
    paths.forEach((path) => add(node, path.representedPlanet, path.source, false));
  });
  return reasons;
}

export function describeRulingPlanetProxyReason(
  reason: RulingPlanetProxyReason,
  planetLabel: (planet: string) => string = (planet) => planet
): string {
  const node = planetLabel(reason.node);
  const planet = planetLabel(reason.planet);
  const relationship: Record<string, string> = {
    REPRESENTS_STAR_LORD: `${planet} is its star lord`,
    REPRESENTS_SIGN_LORD: `${planet} is its sign lord`,
    REPRESENTS_CONJUNCT_PLANET: `${planet} is conjunct with ${node}`,
    REPRESENTS_ASPECTING_PLANET: `${planet} aspects ${node}`,
  };
  const cause = relationship[reason.path] || `of the ${reason.path.toLowerCase().replace(/_/g, ' ')} relationship`;
  return `${node} ${reason.isAcceptedProxy ? 'is proxy for' : 'represents'} ${planet} because ${cause}.`;
}

// Stored current DBA can predate today. Choose a date inside the intersection
// of its displayed periods so all displayed lords stay selected in the popup.
export function getCurrentDbaDate(current?: { periods?: { start?: string; end?: string }[] }): string | undefined {
  const starts = (current?.periods || []).map((period) => period.start ? new Date(period.start).getTime() : NaN).filter(Number.isFinite);
  const ends = (current?.periods || []).map((period) => period.end ? new Date(period.end).getTime() : NaN).filter(Number.isFinite);
  if (!starts.length) return undefined;
  const start = Math.max(...starts);
  const end = ends.length ? Math.min(...ends) : NaN;
  return new Date(Number.isFinite(end) && end > start ? start + (end - start) / 2 : start + 1).toISOString();
}

// Stored and runtime timelines can round a start boundary differently. Open
// inside the selected upcoming window so the popup selects its displayed DBA.
export function getDbaWindowDate(window?: { start?: string; end?: string }): string | undefined {
  if (!window?.start) return undefined;
  const start = new Date(window.start).getTime();
  if (!Number.isFinite(start)) return undefined;
  const end = window.end ? new Date(window.end).getTime() : NaN;
  return new Date(Number.isFinite(end) && end > start ? start + (end - start) / 2 : start + 1000).toISOString();
}

// The complete table includes non-shortlisted and rule-excluded natal planets.
// Final Fruitful and DBA continue to use the unchanged candidate shortlist.
export function getFruitfulPlanetRows<T extends { planet?: string }>(result?: {
  planetResults?: T[]; candidates?: T[]; excludedCandidates?: T[];
} | null): T[] {
  const rows = result?.planetResults || [...(result?.candidates || []), ...(result?.excludedCandidates || [])];
  const seen = new Set<string>();
  return rows.filter((row) => {
    const key = row.planet?.toLowerCase();
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}
